import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ViewedRecordDetails, ViewService } from '../../../../../services/view.service';
import { BehaviorSubject, defer, EMPTY, Subscription } from 'rxjs';
import {
  arr_count,
  arr_filter,
  arr_first,
  arr_map,
  arr_reduce,
  blank,
  data_get,
  data_is_false,
  fallback,
  filled,
  format_datetime,
  map_to_object,
  observe,
  OptionalValue,
  spf,
  to_local_datetime,
  transform
} from '../../../../../shared/utils/common';
import { ApiService } from '../../../../../services/api.service';
import { AssetActualFault } from '../../../types';
import { DIALOG_DATA, DialogService } from '../../../../../services/dialog.service';
import { isUndefined, memoize } from 'lodash-es';
import { compute_asset_displayed_text } from '../../../utils';
import { NotificationService } from '../../../../../services/notification.service';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AssetFaultViewResolutionNotesDialogComponent } from '../../../forms/inputs/actual-faults-input.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'assets-view-job-defects-button',
  template: `
    <fieldmagic-primary-button
      icon="exclamation-circle"
      [label]="(details$ | async) | data_get: 'label'"
      [color]="(details$ | async) | data_get: 'color'"
      tooltip="Click to view defects"
      (click)="onView()"
      [disabled]="fetching$ | async"
    >
    </fieldmagic-primary-button>
  `,
  providers: [
    DialogService,
  ],
})
export class AssetsViewJobDefectsButtonComponent implements OnInit, OnDestroy {
  readonly details$ = new BehaviorSubject<HighestDefectDetails>({
    label: 'No Defects',
  })
  readonly fetching$ = new BehaviorSubject<boolean>(false);

  private _onDefectsInitialisationSubscription?: Subscription;
  private _onViewDetailsChangedSubscription?: Subscription;

  constructor(
    private readonly _view: ViewService,
    private readonly _api: ApiService,
    private readonly _dialog: DialogService,
    private readonly _translator: TranslateService
  ) {}

  ngOnInit(): void {
    this._initDefects(data_get(this._view.getRecord(), 'record_details'));

    this._onViewDetailsChangedSubscription = this._view.details$.subscribe((details) => this._initDefects(details));
  }

  ngOnDestroy(): void {
    if (this._onViewDetailsChangedSubscription) {
      this._onViewDetailsChangedSubscription.unsubscribe();
    }

    if (this._onDefectsInitialisationSubscription) {
      this._onDefectsInitialisationSubscription.unsubscribe();
    }
  }

  onView = () => this._dialog.show({
    component: AssetsViewJobDefectsDialogComponent,
    data: {
      job_id: data_get(this._view.getRecord(), 'record_details.id'),
      customer_id: data_get(this._view.getRecord(), 'record_details.customer_id'),
      site_id: data_get(this._view.getRecord(), 'record_details.site_id'),
      faults: fallback(data_get(this.details$.value, 'faults'), {
        fallback: () => [],
      }),
    },
    custom_settings: {
      minWidth: '95%',
    },
  });

  private _initDefects(details: OptionalValue<ViewedRecordDetails>): void {
    this._onDefectsInitialisationSubscription = observe({
      before: () => this.fetching$.next(true),
      after: () => this.fetching$.next(false),
      observable: () => defer(() => {
        if (blank(details)) {
          return EMPTY;
        }

        return this._api.callPrivate$({
          action: 'post',
          path: '/assets/get_job_faults',
          body: {
            job_id: details.id,
          },
          expectsNewFormat: true,
          onSuccessResponse: (response) => map_to_object(response),
        });
      })
    }).subscribe((data: JobFaultDetailsData) => {
      if (data.count < 1) {
        return this.details$.next({
          color: '#868e96',
          label: this._translator.instant('No Defects'),
        });
      }

      let label = spf(this._translator.instant('%d Defect'), {
        args: [data.count],
      });

      if (data.count > 1) {
        label = spf(this._translator.instant('%d Defects'), {
          args: [data.count],
        });
      }

      return this.details$.next({
        label,
        color: data.color,
        faults: data.faults,
      });
    });
  }
}

@Component({
  selector: 'assets-view-job-defects-dialog',
  template: `
    <fieldmagic-dialog
      label="Defects"
      icon="exclamation-circle"
      [instance]="this"
    >
      <ng-template fieldmagicDialogButtonsTmp>
        <fieldmagic-primary-button
          [label]="(isDisplayingNew$ | async) ? 'Show Existing Defects' : 'Hide Existing Defects'"
          [icon]="(isDisplayingNew$ | async) ? 'eye' : 'eye-slash'"
          [isInProgress]="fetching$ | async"
          [disabled]="(quoting$ | async) || (updating$ | async)"
          (click)="onToggleDefectsList()"
        >
        </fieldmagic-primary-button>

        <fieldmagic-primary-button
          purpose="save"
          [isInProgress]="updating$ | async"
          [disabled]="(quoting$ | async) || (fetching$ | async)"
          (click)="onSave()"
        >
        </fieldmagic-primary-button>

        <fieldmagic-primary-button
          label="Create Quote"
          icon="clipboard-list"
          [isInProgress]="quoting$ | async"
          [disabled]="(updating$ | async) || (fetching$ | async)"
          (click)="onCreateQuote()"
        >
        </fieldmagic-primary-button>
      </ng-template>

      <ng-template fieldmagicDialogContentTmp>
        <div fieldmagicGridRow>
          <div fieldmagicGridColumn="12">
            <table class="table table-bordered">
              <thead>
                <tr>
                  <td>
                    <div class="d-flex justify-content-center">
                      <fieldmagic-checkbox-input
                        [(ngModel)]="selectAll"
                        [useNative]="true"
                        tooltip="Select All"
                        (ngModelChange)="onSelectAll($event)"
                      >
                      </fieldmagic-checkbox-input>
                    </div>
                  </td>
                  <td>
                    <fieldmagic-text
                      content="Asset"
                      purpose="table-header"
                    >
                    </fieldmagic-text>
                  </td>
                  <td>
                    <fieldmagic-text
                      content="Defect"
                      purpose="table-header"
                    >
                    </fieldmagic-text>
                  </td>
                  <td>
                    <fieldmagic-text
                      content="Comments"
                      purpose="table-header"
                    >
                    </fieldmagic-text>
                  </td>
                  <td>
                    <div class="d-flex d-flex-gap align-items-center justify-content-center">
                      <fieldmagic-text
                        content="Acknowledged?"
                        purpose="table-header"
                      >
                      </fieldmagic-text>

                      <fieldmagic-checkbox-input
                        [useNative]="true"
                        [(ngModel)]="acknowledgedAll"
                        tooltip="Click to mark/unmark all as acknowledged"
                        (ngModelChange)="onAcknowledgedAll($event)"
                      >
                      </fieldmagic-checkbox-input>
                    </div>
                  </td>
                  <td>
                    <div class="d-flex d-flex-gap align-items-center justify-content-center">
                      <fieldmagic-text
                        content="Resolved?"
                        purpose="table-header"
                      >
                      </fieldmagic-text>

                      <fieldmagic-checkbox-input
                        [useNative]="true"
                        [(ngModel)]="resolvedAll"
                        tooltip="Click to mark/unmark all as resolved"
                        (ngModelChange)="onResolvedAll($event)"
                      >
                      </fieldmagic-checkbox-input>
                    </div>
                  </td>
                  <td>
                    <fieldmagic-text
                      content="Job"
                      purpose="table-header"
                      alignment="center"
                    >
                    </fieldmagic-text>
                  </td>
                  <td>
                    <fieldmagic-text
                      content="Quote"
                      purpose="table-header"
                      alignment="center"
                    >
                    </fieldmagic-text>
                  </td>
                  <td>
                    <fieldmagic-text
                      content="Date Reported"
                      purpose="table-header"
                    >
                    </fieldmagic-text>
                  </td>
                </tr>
              </thead>

              <ng-container *ngIf="fetching$ | async">
                <tbody>
                  <tr class="text-center">
                    <td colspan="7">
                      <fieldmagic-icon [spin]="true"></fieldmagic-icon>
                    </td>
                  </tr>
                </tbody>
              </ng-container>

              <ng-container *ngIf="! (fetching$ | async)">
                <tbody *ngIf="faults$ | async as faults">
                  <tr
                    *ngIf="faults | blank"
                    class="text-center"
                  >
                    <td colspan="9">{{ 'No defects recorded..' | translate }}</td>
                  </tr>

                  <tr *ngFor="let fault of faults; let fault_index = index;">
                    <td width="1%">
                      <div class="d-flex justify-content-center">
                        <fieldmagic-checkbox-input
                          [(ngModel)]="fault.selected"
                          [useNative]="true"
                          tooltip="Select Asset"
                          (ngModelChange)="onAssetSelect()"
                        >
                        </fieldmagic-checkbox-input>
                      </div>
                    </td>

                    <td width="6%">
                      <a
                        target="_blank"
                        [routerLink]="['/assets', fault.asset_id]"
                      >
                        {{ computeAssetDisplayedText(fault) }}
                      </a>
                    </td>

                    <td
                      width="10%"
                      [style.color]="fault.color | bg_text_color"
                      [style.background]="fault.color"
                    >
                      {{ '(%s) %s' | sprintf: [fault.severity, fault.fault] }}
                    </td>
                    <td width="10%">
                      <fieldmagic-textarea-input
                        [(ngModel)]="fault.comments"
                        [standalone]="true"
                        placeholder="Add comments..."
                        [expandable]="true"
                        (ngModelChange)="emitChanges(fault_index)"
                      >
                      </fieldmagic-textarea-input>
                    </td>
                    <td width="2%">
                      <div class="d-flex d-flex-gap justify-content-center align-items-center">
                        <fieldmagic-checkbox-input
                          [useNative]="true"
                          [(ngModel)]="fault.acknowledged"
                          tooltip="Mark as acknowledged"
                          (ngModelChange)="emitChanges(fault_index)"
                        >
                        </fieldmagic-checkbox-input>

                        <fieldmagic-icon
                          *ngIf="fault.acknowledged && (fault.date_acknowledged | filled)"
                          tooltip="{{ 'Date Acknowledged: %s' | translate | sprintf: [fault.date_acknowledged] }}"
                          [icon]="[{
                            icon: 'info-circle',
                            klass: 'text-info'
                          }]"
                        >
                        </fieldmagic-icon>
                      </div>
                    </td>
                    <td width="2%">
                      <div class="d-flex d-flex-gap justify-content-center align-items-center">
                        <fieldmagic-checkbox-input
                          [useNative]="true"
                          [(ngModel)]="fault.resolved"
                          tooltip="Mark as resolved"
                          (ngModelChange)="emitChanges(fault_index)"
                        >
                        </fieldmagic-checkbox-input>

                        <fieldmagic-icon
                          *ngIf="fault.resolved"
                          [icon]="[{
                            icon: 'info-circle',
                            klass: 'text-info'
                          }]"
                          tooltip="Click to add/view resolution notes"
                          (click)="onAddResolutionNotes(fault_index)"
                        >
                        </fieldmagic-icon>

                      </div>
                    </td>
                    <td width="5%">
                      <div class="d-flex justify-content-center">
                        <fieldmagic-text-link
                          [segments]="(fault.job_text | filled) ? ['/jobs', fault.job_id] : []"
                          [label]="(fault.job_text | filled) ? ('#%06d' | sprintf: [fault.job_text]) : null"
                          [openNewTab]="true"
                        >
                        </fieldmagic-text-link>
                      </div>
                    </td>
                    <td width="5%">
                      <div class="d-flex justify-content-center">
                        <fieldmagic-text-link
                          [segments]="(fault.opportunity_text | filled) ? ['/opportunities', fault.opportunity_id] : []"
                          [label]="(fault.opportunity_text | filled) ? ('#%06d' | sprintf: [fault.opportunity_text]) : null"
                          [openNewTab]="true"
                        >
                        </fieldmagic-text-link>
                      </div>
                    </td>
                    <td width="6%">
                      <div class="d-flex d-flex-gap align-items-center">
                        <span>{{ fault.date_reported }}</span>

                        <fieldmagic-icon
                          [icon]="[{
                            icon: 'info-circle',
                            klass: 'text-info'
                          }]"
                          tooltip="{{ 'Reported By: %s' | translate | sprintf: [fault.reported_by_text] }}"
                        >
                        </fieldmagic-icon>

                        <a
                          *ngIf="fault.image_path | filled"
                          href="{{ fault.image_url }}"
                          target="_blank"
                          matTooltip="{{ 'View Photo' | translate }}"
                          class="btn btn-link"
                        >
                          <fieldmagic-icon
                            icon="file-image"
                          >
                          </fieldmagic-icon>
                        </a>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </ng-container>
            </table>
          </div>
        </div>
      </ng-template>
    </fieldmagic-dialog>
  `
})
export class AssetsViewJobDefectsDialogComponent implements OnInit {
  selectAll: boolean = false;
  acknowledgedAll: boolean = false;
  resolvedAll: boolean = false;

  readonly faults$ = new BehaviorSubject<ViewedJobAssetFault[]>([]);

  readonly quoting$ = new BehaviorSubject<boolean>(false);
  readonly updating$ = new BehaviorSubject<boolean>(false);
  readonly isDisplayingNew$ = new BehaviorSubject<boolean>(true);
  readonly fetching$ = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject(DIALOG_DATA) private readonly _data: AssetsViewJobDefectsDialogData,
    private readonly _api: ApiService,
    private readonly _notifications: NotificationService,
    private readonly _router: Router,
    private readonly _dialog: DialogService,
  ) {}

  ngOnInit(): void {
    this.onToggleDefectsList({
      initial: true,
      cb: () => {
        if (filled(this.faults$.value)) {
          const acknowledged = arr_filter(this.faults$.value, {
            predicate: (fault) => fault.acknowledged,
          });

          const resolved = arr_filter(this.faults$.value, {
            predicate: (fault) => fault.resolved,
          });

          if (arr_count(acknowledged) == arr_count(this.faults$.value)) {
            this.acknowledgedAll = true;
          }

          if (arr_count(resolved) == arr_count(this.faults$.value)) {
            this.resolvedAll = true;
          }
        }
      }
    });
  }

  onSelectAll(checked: boolean): void {
    const faults = [... this.faults$.value];

    for (const index in faults) {
      faults[index] = {
        ... faults[index],
        selected: checked,
      };
    }

    this.faults$.next(faults);
  }

  onAssetSelect = () => this.faults$.next([... this.faults$.value]);

  onCreateQuote = () => observe({
    before: () => this.quoting$.next(true),
    after: () => this.quoting$.next(false),
    observable: () => defer(() => {
      const selected = arr_filter(this.faults$.value, {
        predicate: (fault) => fault.selected,
      });

      if (blank(selected)) {
        this._notifications.notifyError('Please select a defect to quote');
        return EMPTY;
      }

      if (blank(this._data.customer_id)) {
        this._notifications.notifyError('No customer to quote this defects');
        return EMPTY;
      }

      return this._api.callPrivate$({
        action: 'post',
        path: '/assets/quote_faults',
        expectsNewFormat: true,
        body: {
          'faults': arr_map(selected, {
            transformer: (selected) => ({
              id: selected.id,
              asset_id: selected.asset_id,
              asset_name: selected.asset_name,
              asset_serial_number: selected.asset_serial_number,
              asset_type_name: selected.asset_type_name,
              fault: selected.fault,
              job_id: this._data.job_id,
              customer_id: this._data.customer_id,
              site_id: this._data.site_id,
            }),
          })
        },
        onSuccessResponse: (response) => response.get('items'),
      })
    }),
  })
  .pipe(
    filter(filled),
    switchMap((quoted) => this._rebuildFaults().pipe(
      map(() => quoted)
    )),
  )
  .subscribe((quoted) => {
    this._notifications.notifySuccess('Defects quoted successfully.');

    if (arr_count(quoted) > 1 || blank(quoted)) {
      return;
    }

    const quote = arr_first(quoted);

    this._router.navigate(['/opportunities', data_get(quote, 'id')], {
      queryParams: {
        open: data_get(quote, 'quote_version_id'),
      },
    });

    this._dialog.close({
      instance: this,
    });
  });

  onSave = () => observe({
    before: () => this.updating$.next(true),
    after: () => this.updating$.next(false),
    observable: () => defer(() => {
      const selected = arr_filter(this.faults$.value, {
        predicate: (fault) => fault.selected,
      });

      if (blank(selected)) {
        this._notifications.notifyError('Please select a defect to update');
        return EMPTY;
      }

      return this._api.callPrivate$({
        action: 'post',
        path: '/assets/update_faults',
        expectsNewFormat: true,
        body: {
          'faults': arr_reduce(this.faults$.value, {
            initial_value: [],
            reducer: (initial, fault) => {
              initial.push({
                id: fault.id,
                comments: fault.comments,
                resolution_notes: fault.resolution_notes,
                resolved: fault.resolved,
                acknowledged: fault.acknowledged,
              });

              return initial;
            },
          }),
        },
        onSuccessResponse: () => true,
      })
    }),
  })
  .pipe(
    filter(filled),
    switchMap(() => this._rebuildFaults({
      show_existing: ! this.isDisplayingNew$.value,
    })),
  )
  .subscribe(() => this._notifications.notifySuccess('Defects reviewed successfully.'));

  computeAssetDisplayedText = memoize((fault: ViewedJobAssetFault): string => compute_asset_displayed_text({
    name: fault.asset_name,
    serial_number: fault.asset_serial_number,
  }));

  onToggleDefectsList = (args: {
    initial?: boolean,
    cb?: () => void,
  } = {}) => observe({
    before: () => {
      this.fetching$.next(true);

      if (data_is_false(args.initial)) {
        this.isDisplayingNew$.next(! this.isDisplayingNew$.value);
      }
    },
    after: () => this.fetching$.next(false),
    observable: () => this._rebuildFaults({
      show_existing: ! this.isDisplayingNew$.value,
    }),
  }).subscribe(args.cb);

  onAddResolutionNotes(index: number): void {
    if (index < 0) {
      return;
    }

    const faults = this.faults$.value;
    const fault = faults[index];

    return this._dialog.show({
      component: AssetFaultViewResolutionNotesDialogComponent,
      data: {
        fault,
      },
      afterClosing: (result) => {
        if (isUndefined(result)) {
          return;
        }

        faults[index] = {
          ... fault,
          selected: true,
          resolution_notes: data_get(result, 'notes'),
        };

        this.faults$.next([... faults]);
      },
    });
  }

  emitChanges(index: number): void {
    if (index < 0) {
      return;
    }

    const faults = this.faults$.value;

    faults[index] = {
      ... faults[index],
      selected: true,
    };

    this.faults$.next([... faults]);

    const selected = arr_filter(faults, {
      predicate: (fault) => fault.selected,
    });

    this.selectAll = arr_count(selected) == arr_count(faults);
  }

  onAcknowledgedAll(checked: boolean): void {
    const faults = [... this.faults$.value];

    for (const index in faults) {
      faults[index] = {
        ... faults[index],
        acknowledged: checked,
        selected: true,
      };
    }

    this.faults$.next(faults);
    this.selectAll = true;
  }

  onResolvedAll(checked: boolean): void {
    const faults = [... this.faults$.value];

    for (const index in faults) {
      faults[index] = {
        ... faults[index],
        resolved: checked,
        selected: true,
      };
    }

    this.faults$.next(faults);
    this.selectAll = true;
  }

  private _rebuildFaults = (opts: {
    show_existing?: boolean;
  } = {}) => defer(() => {
    opts = Object.assign({
      show_existing: false,
    }, opts);

    return this._api.callPrivate$({
      action: 'post',
      path: '/assets/get_job_faults',
      expectsNewFormat: true,
      body: {
        'job_id': this._data.job_id,
        'show_existing': fallback(data_get(opts, 'show_existing'), {
          fallback: () => false,
        }),
      },
      onSuccessResponse: (response) => arr_map(response.get('faults'), {
        transformer: (fault) => ({
          ... fault,
          selected: false,
          date_reported: transform(data_get(fault, 'date_reported'), {
            transformer: (value: string) => format_datetime(to_local_datetime(value), {
              format: 'll',
            }),
          }),
          date_acknowledged: transform(data_get(fault, 'date_acknowledged'), {
            transformer: (value: string) => format_datetime(to_local_datetime(value), {
              format: 'll',
            }),
          }),
        }),
      }),
    });
  }).pipe(
    tap((faults) => this.faults$.next(faults)),
    tap(() => this.selectAll = false)
  );
}

type HighestDefectDetails = {
  label: string;
  faults?: AssetActualFault[];
  color?: string;
}

type JobFaultDetailsData = {
  count: number;
  color?: string;
  severity?: string;
  faults: AssetActualFault[];
}

type AssetsViewJobDefectsDialogData = {
  job_id: string;
  customer_id?: string;
  site_id?: string;
  faults: AssetActualFault[];
}

type ViewedJobAssetFault = AssetActualFault & {
  selected: boolean;
}